import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  IconButton,
  InputBase,
  MenuItem,
  Popover,
  Select,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from '@mui/material';
import FormatBoldOutlinedIcon from '@mui/icons-material/FormatBoldOutlined';
import FormatItalicOutlinedIcon from '@mui/icons-material/FormatItalicOutlined';
import FormatUnderlinedOutlinedIcon from '@mui/icons-material/FormatUnderlinedOutlined';
import FormatColorTextOutlinedIcon from '@mui/icons-material/FormatColorTextOutlined';
import FormatColorFillOutlinedIcon from '@mui/icons-material/FormatColorFillOutlined';
import FormatAlignLeftOutlinedIcon from '@mui/icons-material/FormatAlignLeftOutlined';
import FormatAlignCenterOutlinedIcon from '@mui/icons-material/FormatAlignCenterOutlined';
import FormatAlignRightOutlinedIcon from '@mui/icons-material/FormatAlignRightOutlined';
import FormatAlignJustifyOutlinedIcon from '@mui/icons-material/FormatAlignJustifyOutlined';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';

import type { TextElement, SlideTextAlign, SlideTextStyle } from '@/shared/types/slide';
import ToolbarColorPicker from '@/shared/components/ToolbarColorPicker';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const MOD =
  typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.userAgent) ? '⌘' : 'Ctrl';

// ---------------------------------------------------------------------------
// Fonts & size presets
// ---------------------------------------------------------------------------

const FONT_FAMILIES = [
  'Arial, sans-serif',
  'Helvetica, sans-serif',
  'Times New Roman, serif',
  'Georgia, serif',
  'Courier New, monospace',
  'Verdana, sans-serif',
  'Trebuchet MS, sans-serif',
  'Impact, sans-serif',
  'Comic Sans MS, cursive',
  'Palatino Linotype, serif',
];

const FONT_FAMILY_BY_PRIMARY_NAME = new Map(
  FONT_FAMILIES.map((fontFamily) => [
    fontFamily
      .split(',')[0]
      .trim()
      .replace(/^['"]|['"]$/g, '')
      .toLowerCase(),
    fontFamily,
  ])
);

function resolveFontFamilySelectValue(value: string | undefined): string {
  if (!value) return 'Arial, sans-serif';
  const primaryFont = value
    .split(',')[0]
    ?.trim()
    .replace(/^['"]|['"]$/g, '')
    .toLowerCase();
  return (primaryFont && FONT_FAMILY_BY_PRIMARY_NAME.get(primaryFont)) ?? value;
}

const ALIGN_OPTIONS: { value: SlideTextAlign; icon: React.ReactNode; label: string }[] = [
  { value: 'left', icon: <FormatAlignLeftOutlinedIcon fontSize="small" />, label: 'Align left' },
  {
    value: 'center',
    icon: <FormatAlignCenterOutlinedIcon fontSize="small" />,
    label: 'Align centre',
  },
  { value: 'right', icon: <FormatAlignRightOutlinedIcon fontSize="small" />, label: 'Align right' },
  { value: 'justify', icon: <FormatAlignJustifyOutlinedIcon fontSize="small" />, label: 'Justify' },
];

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface ToolbarTextStyleProps {
  element: TextElement;
  onUpdateStyle: (patch: Partial<SlideTextStyle>) => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const ToolbarTextStyle: React.FC<ToolbarTextStyleProps> = ({ element, onUpdateStyle }) => {
  // `element` is the RESOLVED selected element (display values); writes go to the
  // stored sparse style via `onUpdateStyle` (§10.11). Tolerate an absent style.
  const style = element.style ?? {};
  const selectedFontFamily = resolveFontFamilySelectValue(style.fontFamily);

  // Alignment popover
  const [alignAnchor, setAlignAnchor] = useState<HTMLElement | null>(null);

  // -- font family ----------------------------------------------------------
  const handleFontFamily = useCallback(
    (e: { target: { value: string } }) => onUpdateStyle({ fontFamily: e.target.value }),
    [onUpdateStyle]
  );

  // -- font size ------------------------------------------------------------
  const fontSize = style.fontSize ?? 18;
  const [fontSizeInput, setFontSizeInput] = useState<string>(String(fontSize));
  const fontSizeFocusedRef = useRef(false);
  useEffect(() => {
    if (!fontSizeFocusedRef.current) {
      setFontSizeInput(String(fontSize));
    }
  }, [fontSize]);
  const commitFontSize = useCallback(() => {
    const n = parseInt(fontSizeInput, 10);
    if (!Number.isNaN(n) && n >= 1 && n <= 200) {
      onUpdateStyle({ fontSize: n });
      setFontSizeInput(String(n));
    } else {
      setFontSizeInput(String(fontSize));
    }
  }, [fontSizeInput, fontSize, onUpdateStyle]);
  const handleFontSizeStep = useCallback(
    (delta: number) => {
      const n = Math.max(1, Math.min(200, fontSize + delta));
      onUpdateStyle({ fontSize: n });
    },
    [fontSize, onUpdateStyle]
  );

  // -- bold / italic / underline --------------------------------------------
  const isBold = style.fontWeight === 'bold' || style.fontWeight === '700';
  const isItalic = style.fontStyle === 'italic';
  const isUnderline = style.textDecoration === 'underline';

  const toggleFormats = (): string[] => {
    const v: string[] = [];
    if (isBold) v.push('bold');
    if (isItalic) v.push('italic');
    if (isUnderline) v.push('underline');
    return v;
  };

  const handleFormat = useCallback(
    (_: React.MouseEvent<HTMLElement>, newFormats: string[]) => {
      onUpdateStyle({
        fontWeight: newFormats.includes('bold') ? 'bold' : 'normal',
        fontStyle: newFormats.includes('italic') ? 'italic' : 'normal',
        textDecoration: newFormats.includes('underline') ? 'underline' : 'none',
      });
    },
    [onUpdateStyle]
  );

  // -- alignment ------------------------------------------------------------
  const currentAlign = style.textAlign ?? 'left';
  const currentAlignOption =
    ALIGN_OPTIONS.find((a) => a.value === currentAlign) ?? ALIGN_OPTIONS[0];

  const handleAlignment = useCallback(
    (value: SlideTextAlign) => {
      onUpdateStyle({ textAlign: value });
      setAlignAnchor(null);
    },
    [onUpdateStyle]
  );

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'nowrap' }}>
      {/* Font family */}
      <Select
        size="small"
        value={selectedFontFamily}
        onChange={handleFontFamily}
        sx={{ minWidth: 110, fontSize: 13, height: 30, '& .MuiSelect-select': { py: 0 } }}
      >
        <MenuItem value="" disabled sx={{ display: 'none' }}>
          Select font
        </MenuItem>
        {FONT_FAMILIES.map((f) => (
          <MenuItem key={f} value={f} sx={{ fontFamily: f, fontSize: 13 }}>
            {f.split(',')[0]}
          </MenuItem>
        ))}
        {!FONT_FAMILIES.includes(selectedFontFamily) && selectedFontFamily && (
          <MenuItem
            value={selectedFontFamily}
            sx={{ fontFamily: selectedFontFamily, fontSize: 13 }}
          >
            {selectedFontFamily.split(',')[0]} ✱
          </MenuItem>
        )}
      </Select>

      {/* Font size — editable input that always shows the current value */}
      <Tooltip title="Decrease font size">
        <IconButton size="small" onClick={() => handleFontSizeStep(-1)}>
          <RemoveIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>
      <InputBase
        value={fontSizeInput}
        onChange={(e) => setFontSizeInput(e.target.value)}
        onFocus={(e) => {
          fontSizeFocusedRef.current = true;
          e.target.select();
        }}
        onBlur={() => {
          fontSizeFocusedRef.current = false;
          commitFontSize();
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            commitFontSize();
            (e.target as HTMLInputElement).blur();
          } else if (e.key === 'Escape') {
            setFontSizeInput(String(fontSize));
            (e.target as HTMLInputElement).blur();
          }
        }}
        inputProps={{
          type: 'text',
          inputMode: 'numeric',
          pattern: '[0-9]*',
          'aria-label': 'Font size',
          style: {
            width: 32,
            textAlign: 'center',
            fontSize: 13,
            fontFamily: 'monospace',
            padding: '2px 0',
            MozAppearance: 'textfield',
          },
        }}
        sx={{
          height: 30,
          border: 1,
          borderColor: 'divider',
          borderRadius: 0.5,
          px: 0.5,
          '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
            WebkitAppearance: 'none',
            m: 0,
          },
        }}
      />
      <Tooltip title="Increase font size">
        <IconButton size="small" onClick={() => handleFontSizeStep(1)}>
          <AddIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>

      {/* Bold / Italic / Underline */}
      <ToggleButtonGroup size="small" value={toggleFormats()} onChange={handleFormat}>
        <ToggleButton value="bold" aria-label="Bold">
          <Tooltip title={`Bold (${MOD}+B)`}>
            <span style={{ display: 'flex' }}>
              <FormatBoldOutlinedIcon fontSize="small" />
            </span>
          </Tooltip>
        </ToggleButton>
        <ToggleButton value="italic" aria-label="Italic">
          <Tooltip title={`Italic (${MOD}+I)`}>
            <span style={{ display: 'flex' }}>
              <FormatItalicOutlinedIcon fontSize="small" />
            </span>
          </Tooltip>
        </ToggleButton>
        <ToggleButton value="underline" aria-label="Underline">
          <Tooltip title={`Underline (${MOD}+U)`}>
            <span style={{ display: 'flex' }}>
              <FormatUnderlinedOutlinedIcon fontSize="small" />
            </span>
          </Tooltip>
        </ToggleButton>
      </ToggleButtonGroup>

      {/* Text color */}
      <ToolbarColorPicker
        value={style.color ?? '#000000'}
        onChange={(c) => onUpdateStyle({ color: c })}
        label="Text color"
        icon={<FormatColorTextOutlinedIcon fontSize="small" />}
      />

      {/* Fill color */}
      <ToolbarColorPicker
        value={style.backgroundColor ?? 'transparent'}
        onChange={(c) => onUpdateStyle({ backgroundColor: c })}
        label="Fill color"
        icon={<FormatColorFillOutlinedIcon fontSize="small" />}
        allowTransparent
      />

      {/* Alignment — collapsed to single icon button with popover */}
      <Tooltip title={currentAlignOption.label}>
        <IconButton size="small" onClick={(e) => setAlignAnchor(e.currentTarget)}>
          {currentAlignOption.icon}
        </IconButton>
      </Tooltip>
      <Popover
        open={Boolean(alignAnchor)}
        anchorEl={alignAnchor}
        onClose={() => setAlignAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Box sx={{ display: 'flex', p: 0.5, gap: 0.25 }}>
          {ALIGN_OPTIONS.map((opt) => (
            <Tooltip key={opt.value} title={opt.label}>
              <IconButton
                size="small"
                color={currentAlign === opt.value ? 'primary' : 'default'}
                onClick={() => handleAlignment(opt.value)}
              >
                {opt.icon}
              </IconButton>
            </Tooltip>
          ))}
        </Box>
      </Popover>
    </Box>
  );
};

export default ToolbarTextStyle;
